import type { AxiosResponse } from 'axios';
import { DIRECTORY_SEARCH_ID_TYPES } from 'Constants/enums';
import { API_ENDPOINTS } from 'Constants/env';
import { IContactApiPagination } from 'Interfaces/contactApiPagination';
import { action, observable, runInAction, makeObservable } from 'mobx';
import { ContactModelV2, IContact as IContactV2 } from 'Models/ContactModelV2';
import {
  ConversationModel,
  IConversationModel,
} from 'Models/ConversationModel';
import { SearchResult } from 'Models/index';
/* CUU2-2822
import { useNotificationStore } from 'Modules/notification/store'; */
import { BaseStore } from 'Stores/BaseStore';
import { RootStore } from 'Stores/RootStore';
import {
  ConversationsMapType,
  SearchableContactModelV2,
  type DirectoryMapType,
  type GetPersonContactSearchResponse,
  type SearchResponseData,
} from 'Stores/SearchStore.types';
import { bugsnagClient } from 'Utils/logUtils';
import API from '../api';

type ALL_SEARCH_ID_TYPES = DIRECTORY_SEARCH_ID_TYPES | 'CONVERSATIONS';

export class SearchStore extends BaseStore {
  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this);
  }

  /** Cache object containing the `query`, `loading` and `data` of each directory search id */
  @observable public conversationsMap: ConversationsMapType = {
    loading: false,
    query: null,
    data: null,
  };

  /** Cache object containing the `query`, `loading` and `data` of each directory search id */
  @observable public directoryMap: DirectoryMapType = {
    GROUP_USERS: { loading: false, query: null, data: null },
    USERS: { loading: false, query: null, data: null },
    DIRECTORY: { loading: false, query: null, data: null },
    TRANSFER: { loading: false, query: null, data: null },
  };

  /** Fetches records for `TRANSFER` and `CONVERSATIONS` directory search ids */
  @action public getPersonContactSearch = async (
    query,
    pageNumber = 1,
    appendResults = false,
    pageSize = 20
  ): Promise<GetPersonContactSearchResponse> => {
    const searchPageNum = pageNumber;

    try {
      this.conversationsMap.loading = true;
      const response = await API.get<string, GetPersonContactSearchResponse>(
        API_ENDPOINTS.Search(
          query,
          ['SearchableDetailsConversation'],
          pageSize,
          searchPageNum
        )
      );

      if (response.status !== 200) {
        this.getSearchResponseError(
          'CONVERSATIONS',
          response.status,
          'getPersonContactSearchSuccess'
        );
        return response;
      }

      const model: SearchResponseData =
        appendResults && this.conversationsMap
          ? {
              hits: this.conversationsMap?.data?.hits,
              results: this.conversationsMap.data?.results,
            }
          : { hits: response.data.hits, results: [] };

      let hasResultTypeError = false;
      response.data?.results?.forEach(({ source, match, score }) => {
        runInAction(() => {
          if (source.searchableType === 'SearchableDetailsConversation') {
            const conv = ConversationModel.FromResponseDto(
              source as IConversationModel
            );
            return model.results.push(new SearchResult(conv, match, score));
          }
          hasResultTypeError = true;
        });
      });

      if (hasResultTypeError) {
        const errMsg =
          'One or more search result(s) have invalid or missing searchableType, this may indicate a problem with the API';
        bugsnagClient.notify(errMsg, (event) => {
          event.severity = 'error';
          event.context = 'SearchStore';
          event.addMetadata('custom', {
            function: 'getPersonContactsSearchSuccess',
          });
        });
      }

      this.conversationsMap = {
        loading: false,
        query,
        data: { hits: model.hits || 0, results: model.results || [] },
      };

      return response;
    } catch (err) {
      this.getDirectorySearchError('CONVERSATIONS', err);
    }
  };

  @action public getDirectorySearch = async (
    directorySearchId: DIRECTORY_SEARCH_ID_TYPES,
    query = '',
    pageNumber = 1,
    appendResults = false,
    pageSize = 20
  ) => {
    return this.getDirectorySearchBase(
      () =>
        API.get<IContactApiPagination<IContactV2>>(
          API_ENDPOINTS.FetchContactsV2({
            sourcePlatforms:
              directorySearchId === 'TRANSFER'
                ? ['Bhive', 'ContactAPI', 'Nylas']
                : ['Bhive'],
            searchTerm: query,
            orderBy: 'name',
            perPage: pageSize,
            page: pageNumber,
          })
        ),
      directorySearchId,
      query,
      appendResults,
      directorySearchId === 'GROUP_USERS' ? 'sourceId' : undefined
    );
  };

  @action public getNextDirectory = async (
    directorySearchId: DIRECTORY_SEARCH_ID_TYPES
  ) => {
    if (!this.directoryMap[directorySearchId].next) {
      return [];
    }

    return this.getDirectorySearchBase(
      () =>
        API.get<IContactApiPagination<IContactV2>>(
          this.directoryMap[directorySearchId].next.replace('http', 'https') // CHANGEME
        ),
      directorySearchId,
      undefined,
      true,
      directorySearchId === 'GROUP_USERS' ? 'sourceId' : undefined
    );
  };

  /** Fetches records for `USERS`, `DIRECTORY` and "TRANSFER" directory search ids */
  @action private getDirectorySearchBase = async (
    fetch: () => Promise<AxiosResponse<IContactApiPagination<IContactV2>>>,
    directorySearchId: DIRECTORY_SEARCH_ID_TYPES,
    query = '',
    appendResults = false,
    idProperty = 'id'
  ): Promise<SearchResult<SearchableContactModelV2>[]> => {
    if (this.directoryMap[directorySearchId].loading) return;

    try {
      this.directoryMap[directorySearchId].loading = true;

      const directoryResponse = await fetch();

      if (directoryResponse.status !== 200) {
        this.getSearchResponseError(
          directorySearchId,
          directoryResponse.status,
          'getDirectorySearchSuccess'
        );

        runInAction(() => {
          this.directoryMap[directorySearchId] = {
            loading: false,
            query,
            data: [],
          };
        });

        return [];
      }

      const directoryContacts = directoryResponse.data.data.map(
        (contact) =>
          new SearchableContactModelV2(ContactModelV2.fromResponseDto(contact))
      );

      const model: SearchResult<SearchableContactModelV2>[] =
        appendResults && this.directoryMap?.[directorySearchId]
          ? this.directoryMap?.[directorySearchId].data ?? []
          : [];

      directoryContacts?.forEach((contact) => {
        runInAction(() => {
          if (!model.some(({ source }) => source.id === contact.id)) {
            contact.searchableType = 'SearchableDetailsPerson';
            model.push(new SearchResult(contact, '', 0.0, idProperty));
          }
        });
      });

      runInAction(() => {
        this.directoryMap[directorySearchId] = {
          loading: false,
          query,
          data: model || [],
          next: directoryResponse.data.links.next,
        };
      });

      return model;
    } catch (err) {
      return this.getDirectorySearchError(directorySearchId, err);
    }
  };

  /** Error message triggered when BE does not return a 200 status code */
  private getSearchResponseError = (
    directorySearchId: ALL_SEARCH_ID_TYPES,
    status,
    errFunction
  ) => {
    if (directorySearchId === 'CONVERSATIONS') {
      this.conversationsMap.loading = false;
    } else {
      this.directoryMap[directorySearchId].loading = false;
    }

    bugsnagClient.notify(
      `${errFunction} response status was ${status}, where 200 was expected. Search results were not processed.`,
      (event) => {
        event.severity = 'error';
        event.context = 'SearchStore';
        event.addMetadata('custom', { function: errFunction });
      }
    );
  };

  /** Error message triggered by the `.catch` of a search store fetch */
  private getDirectorySearchError = (
    directorySearchId: ALL_SEARCH_ID_TYPES,
    err
  ) => {
    if (directorySearchId === 'CONVERSATIONS') {
      this.conversationsMap.loading = false;
    } else {
      this.directoryMap[directorySearchId].loading = false;
    }

    /* CUU2-2822
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error searching',
      message: 'We couldn’t search in the directory. Please refresh the page.',
    }); */

    bugsnagClient.notify('Error Searching Directory', (event) => {
      event.severity = 'error';
      event.context = 'SearchStore';
      event.addMetadata('custom', { function: 'getDirectorySearchError' });
    });

    return err;
  };

  @action clearAllData = () => {
    this.conversationsMap = { loading: false, query: null, data: null };
    this.directoryMap = {
      GROUP_USERS: { loading: false, query: null, data: null },
      USERS: { loading: false, query: null, data: null },
      DIRECTORY: { loading: false, query: null, data: null },
      TRANSFER: { loading: false, query: null, data: null },
    };
  };
}

export default SearchStore;
